import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useFeatures } from '../contexts/FeaturesContext';
import { useView } from '../contexts/ViewContext';
import AdminHeader from '../components/AdminHeader';
import ClientHeader from '../components/ClientHeader';
import NavSidebar from '../components/NavSidebar';
import { usePageMeta } from '../components/DashboardShell';
import { resolvePreset } from '../components/DateRangePicker';

import InfoTip from '../components/shared/InfoTip';
import { ErrorState } from '../components/shared/StateMessage';
import { fmtDate } from '../components/shared/format';
import { fetchAllPages } from '../components/shared/fetchAllPages';
import {
  buildSearchTermCategories,
  buildCampaignsParam,
} from '../components/google-ads/shared';
import {
  CampaignFilterBar,
  GlobalFilterBar,
  HeaderCampaignFilters,
  useCampaignFilterScrollShadow,
} from '../components/google-ads/FilterBar';
import VerdictCard from '../components/google-ads/VerdictCard';
import KpiCards from '../components/google-ads/account-overview/KpiCards';
import BudgetPacing from '../components/google-ads/account-overview/BudgetPacingPanel';
import ConversionBreakdown from '../components/google-ads/account-overview/ConversionBreakdownPanel';
import SpendTrend from '../components/google-ads/campaign-performance/SpendTrendPanel';
import AdCopyInsights from '../components/google-ads/campaign-performance/AdCopyInsightsPanel';
import NonConvertingTerms from '../components/google-ads/campaign-performance/NonConvertingTermsPanel';
import SearchTermExplorer from '../components/google-ads/campaign-performance/SearchTermExplorerPanel';
import CampaignTable from '../components/google-ads/campaign-performance/CampaignTable';
import DevicePerformance from '../components/google-ads/dimensional-breakdowns/DevicePerformancePanel';
import HourOfDay from '../components/google-ads/dimensional-breakdowns/HourOfDayPanel';
import DayOfWeek from '../components/google-ads/dimensional-breakdowns/DayOfWeekPanel';
import GeoPerformance, { PER_PAGE as GEO_PER_PAGE } from '../components/google-ads/dimensional-breakdowns/GeoPerformancePanel';
import { Skeleton, SkeletonChart, SkeletonMetricCard } from '../components/shared/Skeleton';

import {
  getGoogleAdsAdCopyInsights,
  getGoogleAdsBudgetPacing,
  getGoogleAdsCampaigns,
  getGoogleAdsCampaignStatuses,
  getGoogleAdsClientInfo,
  getGoogleAdsConversionsBreakdown,
  getGoogleAdsDimDayOfWeek,
  getGoogleAdsDimDevice,
  getGoogleAdsDimGeo,
  getGoogleAdsDimHourly,
  getGoogleAdsFilters,
  getGoogleAdsKpis,
  getGoogleAdsLastUpdated,
  getGoogleAdsSearchTerms,
  getGoogleAdsSpendTrend,
  getGoogleAdsWastedSpend,
} from '../api/auth';

/* Page skeleton — rendered until the first batch (KPIs + spend trend) lands.
 * Subsequent refetches (date / client / filter changes) keep the prior payloads
 * in state, so the gate stays down and per-section skeletons take over. */
const GoogleAdsPageSkeleton = () => (
  <div className="space-y-4 sm:space-y-6">
    {/* Account Overview header */}
    <Skeleton className="h-4 w-44" />
    {/* Primary + supporting KPI rows (4 + 4) */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {Array.from({ length: 4 }).map((_, i) => <SkeletonMetricCard key={`p${i}`} />)}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {Array.from({ length: 4 }).map((_, i) => <SkeletonMetricCard key={`s${i}`} />)}
    </div>
    {/* Budget pacing bar */}
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-6">
      <Skeleton className="h-8 rounded-full" />
    </div>
    {/* Conversion breakdown */}
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-5">
      <Skeleton className="h-5 w-56 mb-4" />
      <Skeleton className="h-32 rounded-lg" />
    </div>
    {/* Spend trend chart */}
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-6">
      <Skeleton className="h-5 w-48 mb-4" />
      <SkeletonChart height={280} />
    </div>
    {/* Campaign Performance section */}
    <Skeleton className="h-4 w-52" />
    <Skeleton className="h-[360px] rounded-xl" />
    {/* Dimensional Breakdowns section */}
    <Skeleton className="h-4 w-52" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Skeleton className="h-32 rounded-xl" />
      <Skeleton className="h-32 rounded-xl" />
      <Skeleton className="h-32 rounded-xl" />
      <Skeleton className="h-32 rounded-xl" />
    </div>
  </div>
);

const GoogleAdsPage = () => {
  usePageMeta({ headerActive: 'Google Ads', sidebarActive: 'google-ads' });
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { hasFeature } = useFeatures();
  const view = useView();
  const username = localStorage.getItem('username') || 'User';
  const isClient = view.isClient;

  const selectedClientId = view.supabaseClientId;

  /* ----------------------------- Refs ----------------------------- */
  const campaignFilterRef = useRef(null);
  const campaignGroupEndRef = useRef(null);
  const globalFilterRef = useRef(null);
  const showCampaignFiltersInHeader = useCampaignFilterScrollShadow(globalFilterRef, campaignFilterRef, campaignGroupEndRef);

  // Portal target in the app header — campaign filters fold up here once scrolled into the group,
  // so we never stack a second filter bar under the header.
  const [headerSlot, setHeaderSlot] = useState(null);
  useEffect(() => { setHeaderSlot(document.getElementById('app-header-center-slot')); }, []);

  /* ----------------------------- Filter state — dates from ViewContext (single source of truth) ----------------------------- */
  const rangeStart = view.rangeStart;
  const rangeEnd = view.rangeEnd;

  // Default the global date range to "Last 30 Days" when this page first mounts
  // AND the user hasn't already manually picked a range elsewhere. The picker
  // is global (shared via ViewContext), so we only apply our default when no
  // explicit selection exists — otherwise we'd destroy a range the user chose
  // on another page (e.g. Visitor Intelligence). Uses applyDefaultDateRange,
  // which is a no-op once the user has picked and never marks itself as a pick.
  //
  // IMPORTANT (race fix): date-dependent fetches below gate on `noRange`, which is
  // only false once ViewContext reflects a range this page applied (or the user
  // picked). Until the override lands, the global range still holds ViewContext's
  // "This Month" default — so without this gate the first fetch went out for This
  // Month while the header already showed Last 30 Days, and the displayed numbers
  // didn't match the label. Holding fetches until the override lands keeps them in sync.
  const { setDateRange, applyDefaultDateRange } = view;
  const defaultPreset = useMemo(() => resolvePreset("30d"), []);
  const appliedDefaultRange = useRef(false);
  const [rangeOverridden, setRangeOverridden] = useState(false);
  useEffect(() => {
    if (appliedDefaultRange.current) return;
    appliedDefaultRange.current = true;
    if (defaultPreset) {
      applyDefaultDateRange(defaultPreset.start, defaultPreset.end, "30d");
    }
    // Unblock fetches whether or not we applied the default: if we skipped,
    // the user has already chosen a valid range, which is exactly what we
    // want to use — don't hold fetches on the stale first-render range.
    setRangeOverridden(true);
  }, [applyDefaultDateRange, defaultPreset]);

  // Date-dependent fetches may run once our default has landed (or the user changed
  // the range). Guards against the stale "This Month" first render.
  const rangeReady = rangeOverridden && !!rangeStart && !!rangeEnd;
  const noRange = !selectedClientId || !rangeReady;

  // Latest date we actually have data for (e.g. "2026-05-24"). Loaded below.
  const [lastUpdated, setLastUpdated] = useState(null);
  // Presets like "This Month" can run past the latest data date, which would compare a
  // partial current period against a full prior one. Only when the last data date falls
  // *inside* the selected range do we clamp the end to it, so KPI totals and the
  // period-over-period comparison stay like-for-like. If the whole range is beyond the data
  // (e.g. it's June but data is only through April), that's an empty period — don't clamp
  // (it would invert the range); the normal "no data" state handles it.
  // (ISO YYYY-MM-DD strings compare correctly with <.)
  const isPartialPeriod = !!(
    lastUpdated && rangeStart && rangeEnd &&
    lastUpdated >= rangeStart && lastUpdated < rangeEnd
  );
  const effectiveRangeEnd = isPartialPeriod ? lastUpdated : rangeEnd;

  const [comparisonMode, setComparisonMode] = useState('previous_period');
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [statusFilter, setStatusFilter]     = useState('enabled');
  // Dropdown open-state is owned locally by each StatusDropdown/CampaignDropdown in
  // FilterBar.js (not lifted here) — otherwise the two mounted bar instances during
  // header-scroll promotion shared one boolean and opened duplicate menus.

  // Dynamic filter options from API
  const [filterOptions, setFilterOptions] = useState(null);

  // The four fetches below load filter metadata (client info, filter options,
  // campaign statuses, cascaded campaign names). They used to swallow failures
  // into console.error only, which left the filter bar silently empty — the user
  // saw dropdowns with no campaigns and no reason why. They share one banner
  // because they serve one user-facing concern: "the filters didn't load".
  const [filterMetaError, setFilterMetaError] = useState('');

  // Client info for dynamic search term categories
  const [clientInfo, setClientInfo] = useState(null);
  useEffect(() => {
    if (!selectedClientId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getGoogleAdsClientInfo(selectedClientId);
        if (!cancelled) setClientInfo(resp?.data ?? null);
      } catch (e) {
        console.error('Failed to load client info:', e);
        if (!cancelled) setFilterMetaError(e?.message || 'Failed to load client info');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId]);

  const searchTermCategories = useMemo(() => buildSearchTermCategories(clientInfo), [clientInfo]);

  /* ===== FILTER OPTIONS ===== */
  useEffect(() => {
    if (!selectedClientId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getGoogleAdsFilters(selectedClientId, {});
        if (cancelled) return;
        setFilterOptions(resp?.data ?? null);
      } catch (e) {
        console.error('Failed to load filter options:', e);
        if (!cancelled) setFilterMetaError(e?.message || 'Failed to load filter options');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId]);

  // Review #3 — per-campaign status map ({ name: 'ENABLED'|'PAUSED' }). Used to
  // filter the Search Term Explorer by status client-side, since its RPC neither
  // accepts a status param nor returns campaign_status on the rows, AND to cascade
  // the campaign picker below from the status filter. Loaded once per client (status
  // is not date-dependent). Declared BEFORE filteredCampaignNames because that effect
  // reads it (const isn't hoisted — referencing it earlier is a temporal-dead-zone error).
  const [campaignStatusMap, setCampaignStatusMap] = useState({});
  useEffect(() => {
    if (!selectedClientId) { setCampaignStatusMap({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await getGoogleAdsCampaignStatuses(selectedClientId);
        if (!cancelled) setCampaignStatusMap(resp?.data ?? {});
      } catch (e) {
        console.error('Failed to load campaign statuses:', e);
        if (!cancelled) {
          setCampaignStatusMap({});
          setFilterMetaError(e?.message || 'Failed to load campaign statuses');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId]);

  const [filteredCampaignNames, setFilteredCampaignNames] = useState([]);
  useEffect(() => {
    if (!selectedClientId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getGoogleAdsFilters(selectedClientId, {
          status: statusFilter !== 'all' ? statusFilter.toUpperCase() : undefined,
        });
        if (cancelled) return;
        const data = resp?.data ?? null;
        // Cascade the campaign list from the status filter. The RPC
        // (fn_get_gads_filter_options) is status-blind — it always returns the full
        // roster — so scope it client-side against campaignStatusMap (name→status,
        // from fn_get_gads_campaign_settings). Same approach as the Search Term
        // Explorer status filter (Review #3). Unknown-status names are KEPT so a
        // missing map entry never silently empties the picker. (Backend follow-up:
        // add p_status to fn_get_gads_filter_options, then this can read the roster
        // directly.)
        const allNames = data?.campaign_names ?? [];
        const wanted = statusFilter.toUpperCase();
        const names =
          statusFilter === 'all' || !Object.keys(campaignStatusMap).length
            ? allNames
            : allNames.filter((name) => {
                const st = campaignStatusMap[name];
                return st === undefined ? true : st.toUpperCase() === wanted;
              });
        setFilteredCampaignNames(names);
        // Drop any currently-selected campaigns that fell out of the narrowed list.
        setSelectedCampaigns((prev) => {
          const valid = prev.filter((c) => names.includes(c));
          return valid.length !== prev.length ? valid : prev;
        });
      } catch (e) {
        console.error('Failed to load filtered campaign names:', e);
        if (!cancelled) setFilterMetaError(e?.message || 'Failed to load campaign names');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId, statusFilter, campaignStatusMap]);

  // Clear a stale filter-metadata error when the client changes, so a failure for
  // one client doesn't leave the banner up after switching to a working one.
  useEffect(() => { setFilterMetaError(''); }, [selectedClientId]);

  /* ===== ACCOUNT-LEVEL STATE & DATA FETCHING ===== */

  // KPI cards
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountTrend, setAccountTrend] = useState(null);

  // Stale-response guards for the useCallback loaders below. The useEffect fetches
  // on this page use a `cancelled` closure flag, but these loaders are re-created
  // on every filter change and re-invoked from an effect, so a superseded run can
  // resolve after a newer one and overwrite it (rapid statusFilter / campaign
  // changes are the realistic trigger). Each loader bumps its own counter and
  // discards its result if it is no longer the newest.
  const kpisReqRef = useRef(0);
  const wastedReqRef = useRef(0);
  const searchTermsReqRef = useRef(0);
  const campaignsReqRef = useRef(0);

  const loadKpis = useCallback(async () => {
    if (noRange) return;
    const myReq = ++kpisReqRef.current;
    setKpisLoading(true);
    setError('');
    try {
      const resp = await getGoogleAdsKpis(selectedClientId, rangeStart, effectiveRangeEnd, comparisonMode);
      if (myReq !== kpisReqRef.current) return;
      setKpis(resp?.data ?? resp ?? {});
    } catch (error) {
      if (myReq !== kpisReqRef.current) return;
      setError(error?.message || 'Failed to fetch Google Ads KPIs');
      setKpis(null);
    } finally {
      if (myReq === kpisReqRef.current) setKpisLoading(false);
    }
  }, [selectedClientId, rangeStart, effectiveRangeEnd, comparisonMode, noRange]);

  // Account sparkline trend — follows the status filter (drives spend / clicks / CTR / ROAS sparklines)
  useEffect(() => {
    if (noRange) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getGoogleAdsSpendTrend(selectedClientId, rangeStart, rangeEnd, statusFilter !== 'all' ? statusFilter.toUpperCase() : null);
        if (!cancelled) setAccountTrend(resp?.data?.trend ?? []);
      } catch {
        if (!cancelled) setAccountTrend(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId, rangeStart, rangeEnd, statusFilter, noRange]);

  // Bookings sparkline trend — always UNFILTERED, to match the Bookings KPI number
  // (kpis.tier1_bookings is account-wide and ignores the status filter). The backend
  // returns tier1_bookings_estimated=null whenever a campaign filter is applied, so a
  // filtered call here would blank the bookings sparkline; we deliberately omit the
  // status filter. Drives the BOOKINGS and COST PER BOOKING sparklines.
  const [bookingsTrend, setBookingsTrend] = useState(null);
  useEffect(() => {
    if (noRange) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getGoogleAdsSpendTrend(selectedClientId, rangeStart, rangeEnd);
        if (!cancelled) setBookingsTrend(resp?.data?.trend ?? []);
      } catch {
        if (!cancelled) setBookingsTrend(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId, rangeStart, rangeEnd, noRange]);

  // Budget pacing
  const [budgetPacing, setBudgetPacing] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [budgetError, setBudgetError] = useState('');
  const loadBudgetPacing = useCallback(async () => {
    if (!selectedClientId) return;
    setBudgetLoading(true);
    setBudgetError('');
    try {
      // Pacing is a whole-calendar-month concept (MTD spend vs monthly budget), not a
      // function of the selected date range. Anchor it to the latest data date (spec's
      // recommended `latestDataDate`) so "% of month" / "days remaining" reflect now,
      // not the end of the picker's range (which for "This Month" is the last day → 100%).
      const resp = await getGoogleAdsBudgetPacing(selectedClientId, lastUpdated);
      setBudgetPacing(resp?.data ?? resp ?? {});
    } catch (e) {
      setBudgetError(e?.message || 'Failed to load budget pacing');
      setBudgetPacing(null);
    } finally {
      setBudgetLoading(false);
    }
  }, [selectedClientId, lastUpdated]);

  // Conversion breakdown
  const [convBreakdown, setConvBreakdown] = useState(null);
  const [convBreakdownLoading, setConvBreakdownLoading] = useState(false);
  const [convError, setConvError] = useState('');
  useEffect(() => {
    if (noRange) return;
    setConvBreakdownLoading(true);
    setConvError('');
    (async () => {
      try {
        const resp = await getGoogleAdsConversionsBreakdown(selectedClientId, rangeStart, rangeEnd);
        setConvBreakdown(resp?.data ?? resp);
      } catch (error) {
        setConvError(error?.message || 'Failed to load conversion breakdown');
      } finally {
        setConvBreakdownLoading(false);
      }
    })();
  }, [selectedClientId, rangeStart, rangeEnd, noRange]);

  // Ad copy insights
  const [adCopyInsights, setAdCopyInsights] = useState(null);
  const [adCopyLoading, setAdCopyLoading] = useState(false);
  const [adCopyError, setAdCopyError] = useState('');
  useEffect(() => {
    if (noRange) return;
    setAdCopyLoading(true);
    setAdCopyError('');
    (async () => {
      try {
        const resp = await getGoogleAdsAdCopyInsights(selectedClientId, rangeStart, rangeEnd, buildCampaignsParam(selectedCampaigns));
        setAdCopyInsights(resp?.data ?? resp);
      } catch (error) {
        setAdCopyError(error?.message || 'Failed to load ad copy insights');
      } finally {
        setAdCopyLoading(false);
      }
    })();
  }, [selectedClientId, rangeStart, rangeEnd, selectedCampaigns, noRange]);

  // Scope Ad Copy Insights to the active filters client-side. The RPC
  // (fn_get_gads_ad_copy_insights) is campaign/status-blind — it returns every ad
  // for the client+range — but each ad row carries `campaign_name`, so we can filter
  // here. When specific campaigns are picked, keep only their ads; otherwise, when a
  // status is set, keep ads whose campaign matches that status (via campaignStatusMap).
  // Unknown-campaign ads are kept so a missing map entry never empties the panel.
  // (Backend follow-up: add p_campaigns / p_status to the RPC and drop this.)
  const scopedAdCopyInsights = useMemo(() => {
    if (!adCopyInsights) return adCopyInsights;
    const inScope = (ad) => {
      if (selectedCampaigns.length) return selectedCampaigns.includes(ad.campaign_name);
      if (statusFilter !== 'all' && Object.keys(campaignStatusMap).length) {
        const st = campaignStatusMap[ad.campaign_name];
        return st === undefined ? true : st.toUpperCase() === statusFilter.toUpperCase();
      }
      return true;
    };
    return {
      ...adCopyInsights,
      top: (adCopyInsights.top || []).filter(inScope),
      bottom: (adCopyInsights.bottom || []).filter(inScope),
    };
  }, [adCopyInsights, selectedCampaigns, statusFilter, campaignStatusMap]);

  useEffect(() => { loadKpis(); }, [loadKpis]);
  useEffect(() => { loadBudgetPacing(); }, [loadBudgetPacing]);

  /* ===== CAMPAIGN-LEVEL STATE & DATA FETCHING ===== */

  // Spend trend
  const [spendTrend, setSpendTrend] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState('');
  const loadSpendTrend = useCallback(async () => {
    if (noRange) return;
    setTrendLoading(true);
    setTrendError('');
    try {
      const resp = await getGoogleAdsSpendTrend(selectedClientId, rangeStart, rangeEnd, statusFilter !== 'all' ? statusFilter.toUpperCase() : null, buildCampaignsParam(selectedCampaigns));
      setSpendTrend(resp?.data?.trend ?? []);
    } catch (e) {
      setTrendError(e?.message || 'Failed to load spend trend');
      setSpendTrend(null);
    } finally {
      setTrendLoading(false);
    }
  }, [selectedClientId, rangeStart, rangeEnd, statusFilter, selectedCampaigns, noRange]);

  // Wasted spend (paginated aggregation)
  const [wastedSpend, setWastedSpend] = useState(null);
  const [wastedLoading, setWastedLoading] = useState(false);
  const [wastedError, setWastedError] = useState('');
  const loadWastedSpend = useCallback(async () => {
    if (noRange) return;
    const myReq = ++wastedReqRef.current;
    setWastedLoading(true);
    setWastedError('');
    try {
      const baseParams = {
        limit: 100,
        sort_by: 'cost',
        sort_dir: 'desc',
        campaign_status: statusFilter !== 'all' ? statusFilter.toUpperCase() : undefined,
        campaign_type: campaignFilter !== 'all' ? campaignFilter.toUpperCase() : undefined,
        campaigns: buildCampaignsParam(selectedCampaigns),
      };
      // Keep page 1's envelope so we can preserve its non-item fields (pagination,
      // totals) on the merged result; fetchAllPages runs the shared paging loop.
      let firstData = {};
      const allItems = await fetchAllPages(async (page) => {
        const data = (await getGoogleAdsWastedSpend(selectedClientId, rangeStart, rangeEnd, { ...baseParams, page }))?.data ?? {};
        if (page === 1) firstData = data;
        return data;
      });
      if (myReq !== wastedReqRef.current) return;
      setWastedSpend({ ...firstData, items: allItems });
    } catch (e) {
      if (myReq !== wastedReqRef.current) return;
      setWastedError(e?.message || 'Failed to load non-converting terms');
      setWastedSpend(null);
    } finally {
      if (myReq === wastedReqRef.current) setWastedLoading(false);
    }
  }, [selectedClientId, rangeStart, rangeEnd, statusFilter, campaignFilter, selectedCampaigns, noRange]);

  // Search terms (paginated aggregation)
  const [searchTerms, setSearchTerms] = useState(null);
  const [searchTermsLoading, setSearchTermsLoading] = useState(false);
  const [searchTermsError, setSearchTermsError] = useState('');
  const loadSearchTerms = useCallback(async () => {
    if (noRange) return;
    const myReq = ++searchTermsReqRef.current;
    setSearchTermsLoading(true);
    setSearchTermsError('');
    try {
      const baseParams = {
        limit: 100,
        sort_by: 'cost',
        sort_dir: 'desc',
        campaign_status: statusFilter !== 'all' ? statusFilter.toUpperCase() : undefined,
        campaign_type: campaignFilter !== 'all' ? campaignFilter.toUpperCase() : undefined,
        campaigns: buildCampaignsParam(selectedCampaigns),
      };
      // Keep page 1's envelope so its non-item fields survive on the merged result;
      // fetchAllPages runs the shared paging loop. `allItems` stays a `let` — the
      // Review #3 status filter below reassigns it.
      let firstData = {};
      let allItems = await fetchAllPages(async (page) => {
        const data = (await getGoogleAdsSearchTerms(selectedClientId, rangeStart, rangeEnd, { ...baseParams, page }))?.data ?? {};
        if (page === 1) firstData = data;
        return data;
      });
      // Review #3 — apply the status filter client-side. The RPC can't filter by
      // status, so drop rows whose campaign isn't in the selected status. Only
      // filter when we actually have a status map for the campaign (unknown
      // campaigns are kept, so a missing map never silently empties the table).
      if (statusFilter !== 'all' && Object.keys(campaignStatusMap).length) {
        const wanted = statusFilter.toUpperCase();
        allItems = allItems.filter((item) => {
          const st = campaignStatusMap[item.campaign_name];
          return st === undefined ? true : st === wanted;
        });
      }
      if (myReq !== searchTermsReqRef.current) return;
      setSearchTerms({ ...firstData, items: allItems });
    } catch (e) {
      if (myReq !== searchTermsReqRef.current) return;
      setSearchTermsError(e?.message || 'Failed to load search terms');
      setSearchTerms(null);
    } finally {
      if (myReq === searchTermsReqRef.current) setSearchTermsLoading(false);
    }
  }, [selectedClientId, rangeStart, rangeEnd, statusFilter, campaignFilter, selectedCampaigns, campaignStatusMap, noRange]);

  // Campaign performance
  const [campaigns, setCampaigns] = useState(null);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState('');
  const [campSortBy, setCampSortBy] = useState('cost');
  const [campSortDir, setCampSortDir] = useState('desc');
  const [campPage, setCampPage] = useState(1);
  const campPerPage = 25;

  const loadCampaigns = useCallback(async () => {
    if (noRange) return;
    const myReq = ++campaignsReqRef.current;
    setCampaignsLoading(true);
    setCampaignsError('');
    try {
      const resp = await getGoogleAdsCampaigns(selectedClientId, rangeStart, rangeEnd, {
        campaign_status: statusFilter !== 'all' ? statusFilter.toUpperCase() : undefined,
        campaign_type: campaignFilter !== 'all' ? campaignFilter.toUpperCase() : undefined,
        campaigns: buildCampaignsParam(selectedCampaigns),
        sort_by: campSortBy,
        sort_dir: campSortDir,
        page: campPage,
        limit: campPerPage,
      });
      if (myReq !== campaignsReqRef.current) return;
      setCampaigns(resp?.data ?? {});
    } catch (e) {
      if (myReq !== campaignsReqRef.current) return;
      setCampaignsError(e?.message || 'Failed to load campaigns');
      setCampaigns(null);
    } finally {
      if (myReq === campaignsReqRef.current) setCampaignsLoading(false);
    }
  }, [selectedClientId, rangeStart, rangeEnd, statusFilter, campaignFilter, selectedCampaigns, campSortBy, campSortDir, campPage, noRange]);

  // Reset to page 1 when the dataset identity changes. Campaigns are SERVER-paged,
  // so requesting page 8 of a filtered set that now has 2 pages returns an empty
  // envelope, which the table renders as "No campaign data found" — a false empty
  // state for a client that does have campaigns. Sorting resets separately in
  // handleCampSort, which also flips sort direction.
  useEffect(() => {
    setCampPage(1);
  }, [selectedClientId, rangeStart, rangeEnd, statusFilter, campaignFilter, selectedCampaigns]);

  const handleCampSort = (col) => {
    if (campSortBy === col) {
      setCampSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setCampSortBy(col);
      setCampSortDir('desc');
    }
    setCampPage(1);
  };

  // Dimensional breakdowns
  const [dimExpanded, setDimExpanded] = useState({ device: false, hourly: false, dayOfWeek: false, geo: false });
  const [deviceData, setDeviceData] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [dayOfWeekData, setDayOfWeekData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [dimLoading, setDimLoading] = useState(false);
  const [dimError, setDimError] = useState('');
  const [geoPage, setGeoPage] = useState(1);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');

  // Device / Hourly / DayOfWeek return their full result set in one call, so they
  // refetch only when the client, range, or campaign filter changes. Geo is
  // SERVER-paginated and therefore lives in its own effect below — folding it in
  // here would mean every page change refetched all four breakdowns.
  useEffect(() => {
    if (noRange) return;
    let cancelled = false;
    (async () => {
      setDimLoading(true);
      setDimError('');
      try {
        const campParam = buildCampaignsParam(selectedCampaigns);
        const [devResp, hrResp, dowResp] = await Promise.all([
          getGoogleAdsDimDevice(selectedClientId, rangeStart, rangeEnd, campParam),
          getGoogleAdsDimHourly(selectedClientId, rangeStart, rangeEnd, campParam),
          getGoogleAdsDimDayOfWeek(selectedClientId, rangeStart, rangeEnd, campParam),
        ]);
        if (!cancelled) {
          setDeviceData(devResp?.data ?? devResp);
          setHourlyData(hrResp?.data ?? hrResp);
          setDayOfWeekData(dowResp?.data ?? dowResp);
        }
      } catch (error) {
        if (!cancelled) setDimError(error?.message || 'Failed to load dimensional breakdowns');
      } finally {
        if (!cancelled) setDimLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId, rangeStart, rangeEnd, selectedCampaigns, noRange]);

  // Geo — server-paginated (fn_get_gads_geo_performance takes p_page/p_limit).
  // `geoPage` was previously absent from the fetch entirely (a literal 1 was
  // passed), so the Pagination control advanced its label while the rows never
  // changed. It is now both an argument and a dependency.
  useEffect(() => {
    if (noRange) return;
    let cancelled = false;
    (async () => {
      setGeoLoading(true);
      setGeoError('');
      try {
        const resp = await getGoogleAdsDimGeo(
          selectedClientId, rangeStart, rangeEnd, geoPage, GEO_PER_PAGE, buildCampaignsParam(selectedCampaigns),
        );
        if (!cancelled) setGeoData(resp?.data ?? resp);
      } catch (error) {
        if (!cancelled) setGeoError(error?.message || 'Failed to load geographic performance');
      } finally {
        if (!cancelled) setGeoLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId, rangeStart, rangeEnd, selectedCampaigns, geoPage, noRange]);

  // Reset to page 1 when the dataset identity changes, so narrowing the range or
  // switching client can't leave the user requesting a page that no longer exists.
  useEffect(() => {
    setGeoPage(1);
  }, [selectedClientId, rangeStart, rangeEnd, selectedCampaigns]);

  const toggleDimSection = (key) => {
    setDimExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => { loadSpendTrend(); }, [loadSpendTrend]);
  useEffect(() => { loadWastedSpend(); }, [loadWastedSpend]);
  useEffect(() => { loadSearchTerms(); }, [loadSearchTerms]);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  /* Last updated timestamp (state declared above so the date clamp can use it) */
  useEffect(() => {
    if (!selectedClientId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getGoogleAdsLastUpdated(selectedClientId);
        if (cancelled) return;
        setLastUpdated(resp?.data?.latest_data_date ?? null);
      } catch {
        if (!cancelled) setLastUpdated(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId]);

  const hasData = kpis && (kpis.spend > 0 || kpis.clicks > 0 || kpis.all_conversions > 0);

  // First-load gate: page renders a unified skeleton until the KPI payload
  // lands. KPIs are typically the fastest fetch, so gating on them alone gives
  // the "everything loads together" feel without making the page wait for the
  // slowest fetch — the spend trend (and other sections) finish their own
  // per-section skeletons as they arrive. Refetches (date / filter / client
  // changes) keep the prior payloads in state, so the gate stays down on
  // subsequent loads.
  const firstBatchLoading = kpisLoading && !kpis;

  return (
    <main className="flex-1 min-w-0 px-4 md:px-6 lg:px-10 py-6 space-y-6 overflow-x-clip">
          <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Google Ads Performance</h1>
            {lastUpdated && (
              <span className="text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                Data through: {new Date(`${lastUpdated}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>

          {!view.supabaseClientId && (
            <GlobalFilterBar
              ref={globalFilterRef}
              localRangeStart={rangeStart}
              setLocalRangeStart={(v) => view.setDateRange(v, view.rangeEnd)}
              localRangeEnd={rangeEnd}
              setLocalRangeEnd={(v) => view.setDateRange(view.rangeStart, v)}
              showCampaignFiltersInHeader={showCampaignFiltersInHeader}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              filterOptions={filterOptions}
              selectedCampaigns={selectedCampaigns}
              setSelectedCampaigns={setSelectedCampaigns}
              filteredCampaignNames={filteredCampaignNames}
            />
          )}

          {error && <ErrorState variant="banner" message={error} />}

          {/* Filters failed to load — the data below is unfiltered, so say so
              rather than leaving empty dropdowns unexplained. */}
          {filterMetaError && (
            <ErrorState
              variant="banner"
              message={`Some filter options couldn't be loaded (${filterMetaError}). Campaign and status filters may be incomplete.`}
            />
          )}

          {kpis && !hasData && !kpisLoading && (
            <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-gray-500 dark:text-gray-400 text-sm text-center">
              No Google Ads data for this period.
            </div>
          )}

          {firstBatchLoading ? <GoogleAdsPageSkeleton /> : (<>
          {/* Top-level insight card — quadrant verdict with AI narrative covering position + deltas */}
          {kpis && hasData && (
            <VerdictCard
              kpis={kpis}
              clientId={selectedClientId}
              startDate={rangeStart}
              endDate={effectiveRangeEnd}
              comparisonMode={comparisonMode}
            />
          )}

          {/* ============ ACCOUNT OVERVIEW GROUP ============ */}
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 inline-flex items-center">
            <span>Account Overview</span>
            <InfoTip text="Account-level totals across all campaigns and statuses for the range. Filters in the section below don't affect these numbers." />
          </h2>

          <KpiCards
            kpis={kpis}
            loading={kpisLoading}
            accountTrend={accountTrend}
            bookingsTrend={bookingsTrend}
            comparisonMode={comparisonMode}
            setComparisonMode={setComparisonMode}
            rangeStart={rangeStart}
            rangeEnd={effectiveRangeEnd}
            isPartialPeriod={isPartialPeriod}
            dataThrough={lastUpdated}
          />

          <BudgetPacing
            budgetPacing={budgetPacing}
            loading={budgetLoading}
            error={budgetError}
            referenceDate={lastUpdated}
          />

          <ConversionBreakdown
            convBreakdown={convBreakdown}
            loading={convBreakdownLoading}
            error={convError}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
          />

          {/* ============ CAMPAIGN PERFORMANCE GROUP ============ */}
          <CampaignFilterBar
            ref={campaignFilterRef}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            filterOptions={filterOptions}
            selectedCampaigns={selectedCampaigns}
            setSelectedCampaigns={setSelectedCampaigns}
            filteredCampaignNames={filteredCampaignNames}
          />

          {/* While scrolled into this group, fold the campaign filters into the app
              header's center slot instead of stacking a second sticky bar below it. */}
          {view.supabaseClientId && showCampaignFiltersInHeader && headerSlot && createPortal(
            <HeaderCampaignFilters
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              filterOptions={filterOptions}
              selectedCampaigns={selectedCampaigns}
              setSelectedCampaigns={setSelectedCampaigns}
              filteredCampaignNames={filteredCampaignNames}
            />,
            headerSlot,
          )}

          <SpendTrend spendTrend={spendTrend} loading={trendLoading} error={trendError} />

          <AdCopyInsights adCopyInsights={scopedAdCopyInsights} loading={adCopyLoading} error={adCopyError} />

          <NonConvertingTerms
            wastedSpend={wastedSpend}
            loading={wastedLoading}
            error={wastedError}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
          />

          <SearchTermExplorer
            searchTerms={searchTerms}
            loading={searchTermsLoading}
            error={searchTermsError}
            searchTermCategories={searchTermCategories}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
          />

          <CampaignTable
            campaigns={campaigns}
            loading={campaignsLoading}
            error={campaignsError}
            sortBy={campSortBy}
            sortDir={campSortDir}
            onSort={handleCampSort}
            page={campPage}
            perPage={campPerPage}
            onPageChange={setCampPage}
            selectedClientId={selectedClientId}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
          />

          {/* ============ DIMENSIONAL BREAKDOWNS ============ */}
          {/* ref marks the group end — releases the header-folded filters once this section reaches the header */}
          <div ref={campaignGroupEndRef} className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 inline-flex items-center">
              <span>Dimensional Breakdowns</span>
              <InfoTip text="Performance sliced by device, hour of day, day of week, and location. Use to find a segment that's outperforming or dragging down the average." />
            </h2>
            <DevicePerformance
              deviceData={deviceData}
              loading={dimLoading}
              error={dimError}
              expanded={dimExpanded.device}
              onToggle={() => toggleDimSection('device')}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
            />
            <HourOfDay
              hourlyData={hourlyData}
              loading={dimLoading}
              error={dimError}
              expanded={dimExpanded.hourly}
              onToggle={() => toggleDimSection('hourly')}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
            />
            <DayOfWeek
              dayOfWeekData={dayOfWeekData}
              loading={dimLoading}
              error={dimError}
              expanded={dimExpanded.dayOfWeek}
              onToggle={() => toggleDimSection('dayOfWeek')}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
            />
            <GeoPerformance
              geoData={geoData}
              loading={geoLoading}
              error={geoError}
              expanded={dimExpanded.geo}
              onToggle={() => toggleDimSection('geo')}
              page={geoPage}
              onPageChange={setGeoPage}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
            />
          </div>
          </>)}
        </main>
  );
};

export default GoogleAdsPage;
